// src/components/ProjectTeamCard.tsx
import { useMemo } from 'react';
import { useAtom } from 'jotai';
import { userAllocationsAtom, projectsAtom } from '@/lib/store';
import { useProjectHighlight } from '@/hooks/useProjectHighlight';

// Import the same color array used in UserStoryPointChart
const PROJECT_COLORS = [
  'bg-primary', 'bg-green-500', 'bg-yellow-500', 'bg-red-500',
  'bg-purple-500', 'bg-pink-500', 'bg-indigo-500', 'bg-blue-400',
  'bg-orange-500', 'bg-teal-500', 'bg-cyan-500', 'bg-lime-500'
];

export default function ProjectTeamCard() {
  const [userAllocations] = useAtom(userAllocationsAtom);
  const [projects] = useAtom(projectsAtom);
  const { 
    highlightedProject, 
    highlightProject, 
    clearHighlight,
    isUserHighlighted
  } = useProjectHighlight();

  // Create a map of project keys to project names
  const projectNameMap = useMemo(() => {
    const map = new Map<string, string>();
    projects.forEach(project => {
      map.set(project.key, project.name);
    });
    return map;
  }, [projects]);

  // Extract project data with team members
  const projectData = useMemo(() => {
    // First collect all unique project keys and their story points
    const projectStoryPoints = new Map<string, number>();
    const projectMembers = new Map<string, Set<string>>();
    const projectCompletedPoints = new Map<string, number>();
    
    userAllocations.forEach(allocation => {
      Object.values(allocation.sprints).forEach(sprintData => {
        sprintData.issues.forEach(issue => {
          if (!issue.storyPoints) return;
          
          // Extract project key from issue key (e.g., "ABC-123" -> "ABC")
          const projectKey = issue.key.split('-')[0];
          
          // Track story points
          const currentPoints = projectStoryPoints.get(projectKey) || 0;
          projectStoryPoints.set(projectKey, currentPoints + issue.storyPoints);
          
          // Track completed points
          if (issue.done) {
            const currentCompleted = projectCompletedPoints.get(projectKey) || 0;
            projectCompletedPoints.set(projectKey, currentCompleted + issue.storyPoints);
          }
          
          // Track team members
          if (!projectMembers.has(projectKey)) {
            projectMembers.set(projectKey, new Set());
          }
          projectMembers.get(projectKey)?.add(allocation.user.id);
        });
      });
    });
    
    // Get all project keys and sort them alphabetically
    const projectKeys = Array.from(projectStoryPoints.keys()).sort();
    
    // Create project data with colors based on alphabetical order
    return projectKeys.map((projectKey, index) => {
      return {
        key: projectKey,
        name: projectNameMap.get(projectKey) || projectKey,
        color: PROJECT_COLORS[index % PROJECT_COLORS.length],
        totalPoints: projectStoryPoints.get(projectKey) || 0,
        completedPoints: projectCompletedPoints.get(projectKey) || 0,
        members: Array.from(projectMembers.get(projectKey) || [])
          .map(userId => userAllocations.find(allocation => allocation.user.id === userId)?.user)
          .filter(Boolean)
      };
    }).sort((a, b) => a.key.localeCompare(b.key)); // Sort by project key alphabetically
  }, [userAllocations, projectNameMap]);

  if (projectData.length === 0) {
    return <></>;
  }

  return (
    <div className="mt-6 p-4 bg-white dark:bg-gray-800 rounded-lg shadow">
      <h2 className="text-xl font-bold mb-1">Project Teams</h2>
      <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">{projectData.length} projects with active story points</p>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {projectData.map(project => (
          <div 
            key={project.key}
            className={`bg-gray-50 dark:bg-gray-700 rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-all duration-200 ${
              highlightedProject !== null && highlightedProject !== project.key ? 'opacity-30' : 'opacity-100'
            }`}
            onMouseEnter={() => highlightProject(project.key, userAllocations)}
            onMouseLeave={() => clearHighlight()}
          >
            <div className={`${project.color} h-2 w-full ${
              highlightedProject === project.key ? 'ring-1 ring-white dark:ring-gray-300' : ''
            }`}></div>
            <div className="p-4">
              <div className="flex justify-between items-center mb-3">
                <h3 className={`text-lg font-bold truncate ${
                  highlightedProject === project.key ? 'text-primary' : ''
                }`} title={project.name}>
                  {project.key}
                </h3>
                <div className="text-sm text-gray-500 dark:text-gray-300">
                  {Math.round((project.completedPoints / project.totalPoints) * 100)}% Complete
                </div>
              </div>
              
              {project.name !== project.key && (
                <div className="text-sm text-gray-600 dark:text-gray-300 mb-3 truncate" title={project.name}>
                  {project.name}
                </div>
              )}
              
              <div className="mb-3 bg-gray-200 dark:bg-gray-600 rounded-full h-2.5">
                <div 
                  className="bg-green-400 h-2.5 rounded-full" 
                  style={{ width: `${(project.completedPoints / project.totalPoints) * 100}%` }}
                ></div>
              </div>
              
              <div className="text-sm text-gray-600 dark:text-gray-300 mb-3">
                <span className="font-medium">{project.completedPoints}/{project.totalPoints}</span> story points
              </div>
              
              <div className="mt-4">
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Team Members</h4>
                <div className="flex flex-wrap">
                  {project.members.filter(m => m != undefined).slice(0, 5).map((user, index) => {
                    const isUserHighlightedInProject = highlightedProject === null || 
                                                      (highlightedProject === project.key && isUserHighlighted(user.id));
                    return (
                      <div key={user.id} className="relative -ml-2 first:ml-0" style={{zIndex: 10 - index}}>
                        {user.avatarUrl ? (
                            <img
                                src={user.avatarUrl}
                                alt={user.displayName}
                                className={`w-8 h-8 rounded-full border-2 ${
                                  isUserHighlightedInProject && highlightedProject === project.key 
                                    ? 'border-primary ring-1 ring-primary' 
                                    : 'border-white dark:border-gray-700'
                                } transition-all duration-200`}
                                title={user.displayName}
                            />
                        ) : (
                            <div
                                className={`w-8 h-8 rounded-full bg-gray-300 dark:bg-gray-600 flex items-center justify-center text-xs font-medium border-2 ${
                                  isUserHighlightedInProject && highlightedProject === project.key 
                                    ? 'border-primary ring-1 ring-primary' 
                                    : 'border-white dark:border-gray-700'
                                } transition-all duration-200`}
                                title={user.displayName}
                            >
                              {user.displayName.charAt(0)}
                            </div>
                        )}
                      </div>
                    );
                  })}
                  
                  {project.members.length > 5 && (
                    <div 
                      className={`w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-600 flex items-center justify-center text-xs -ml-2 border-2 ${
                        highlightedProject === project.key 
                          ? 'border-primary ring-1 ring-primary' 
                          : 'border-white dark:border-gray-700'
                      } transition-all duration-200`}
                      title={`${project.members.length - 5} more team members`}
                    >
                      +{project.members.length - 5}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}